import { useState, type KeyboardEvent, type MouseEvent } from "react";
import type { NodeDto } from "@mmm/contracts";
import { Icon, useT } from "@mmm/ui";
interface NodeCardProps {
  node: NodeDto;
  hasWriting: boolean;
  selected: boolean;
  connectSource: boolean;
  connectMode: boolean;
  onMouseDown: (event: MouseEvent) => void;
  onClick: (event: MouseEvent) => void;
  onRename: (title: string) => void;
}

export function NodeCard({ node, hasWriting, selected, connectSource, connectMode, onMouseDown, onClick, onRename }: NodeCardProps) {
  const t = useT();
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(node.title);

  const commitRename = () => {
    setRenaming(false);
    const title = draft.trim();
    if (title && title !== node.title) {
      onRename(title);
      return;
    }
    if (!title) setDraft(node.title);
  };

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "Enter") event.currentTarget.blur();
    if (event.key === "Escape") {
      setDraft(node.title);
      setRenaming(false);
    }
  };

  return (
    <div
      className="mmm-node-card"
      style={{
        position: "absolute",
        left: node.x,
        top: node.y,
        width: node.width,
        minHeight: node.height,
        borderTop: `2px solid ${selected || connectSource ? "var(--mmm-gold)" : "var(--mmm-node-border)"}`,
        borderRight: `2px solid ${selected || connectSource ? "var(--mmm-gold)" : "var(--mmm-node-border)"}`,
        borderBottom: `2px solid ${selected || connectSource ? "var(--mmm-gold)" : "var(--mmm-node-border)"}`,
        borderLeft: `5px solid ${node.color}`,
        borderRadius: 10,
        background: "var(--mmm-card)",
        boxShadow: "var(--mmm-shadow-node)",
        padding: "10px 14px",
        cursor: connectMode ? "crosshair" : "grab",
        userSelect: "none",
        fontSize: 14,
        fontWeight: 600,
      }}
      onMouseDown={onMouseDown}
      onClick={onClick}
      onDoubleClick={(event) => {
        event.stopPropagation();
        setDraft(node.title);
        setRenaming(true);
      }}
    >
      <div className="mmm-node-card-title-row">
        {renaming ? (
          <input
            autoFocus
            className="mmm-node-title-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commitRename}
            onKeyDown={handleRenameKeyDown}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          />
        ) : (
          <span className="mmm-node-card-title">{node.title}</span>
        )}
        {!hasWriting && !renaming && (
          <span
            className="mmm-node-empty-writing-icon"
            role="img"
            aria-label={t("maps.noNotesYet")}
            title={t("maps.noNotesYet")}
          >
            <Icon name="star" strokeWidth={1.2} />
          </span>
        )}
      </div>
      {node.collapsed && (
        <div style={{ fontSize: 11, fontWeight: 500, color: "var(--mmm-muted)", marginTop: 2 }}>{t("maps.collapsed")}</div>
      )}
    </div>
  );
}
